import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { castList } from '@/mocks/siteData';
import CastCard from '@/components/feature/CastCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Pagination from '@/components/feature/Pagination';
import SiteSidebar from '@/components/feature/SiteSidebar';

const PAGE_SIZE = 8;

export default function CastCategory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlKeyword = (searchParams.get('q') || '').trim();
  const [input, setInput] = useState(urlKeyword);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setInput(urlKeyword);
  }, [urlKeyword]);

  useEffect(() => {
    document.title = '善良的小姨子 - 演员表与角色介绍';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        'content',
        '《善良的小姨子》演员表与角色介绍，查看主要演员及其饰演角色的详细资料。'
      );
    }
  }, []);

  const filtered = useMemo(() => {
    if (!urlKeyword) return castList;
    const kw = urlKeyword.toLowerCase();
    return castList.filter(
      (c) => c.name.toLowerCase().includes(kw) || c.role.toLowerCase().includes(kw)
    );
  }, [urlKeyword]);

  useEffect(() => {
    setPage(1);
  }, [urlKeyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (q) {
      setSearchParams({ q });
    } else {
      setSearchParams({});
    }
  };

  const reset = () => {
    setInput('');
    setSearchParams({});
  };

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-10 animate-fade-up">
      <Breadcrumb items={[{ label: '演员列表' }]} />

      <div className="mt-5 mb-8">
        <h1 className="font-heading font-bold text-foreground-950 text-2xl md:text-4xl mb-3">
          善良的小姨子 - 演员表与角色介绍
        </h1>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
          认识《善良的小姨子》的每一位演员及其饰演角色，点击演员卡片查看角色介绍与相关剧集。
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <SiteSidebar />

        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 pb-4 border-b border-background-200">
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <i className="ri-team-line w-5 h-5 flex items-center justify-center text-primary-600"></i>
              {urlKeyword ? (
                <span>
                  关键词「<span className="text-accent-700 font-medium">{urlKeyword}</span>」共找到{' '}
                  <span className="text-foreground-950 font-semibold">{filtered.length}</span> 位演员
                </span>
              ) : (
                <span>
                  共 <span className="text-foreground-950 font-semibold">{castList.length}</span> 位演员
                </span>
              )}
            </div>
            <form onSubmit={handleSearch} className="flex items-center gap-2 w-full sm:w-auto">
              <div className="flex items-center flex-1 sm:w-56 bg-background-100 border border-background-200 rounded-full px-3.5 h-10 focus-within:border-accent-400 transition-colors">
                <i className="ri-search-line text-foreground-500 text-sm w-5 h-5 flex items-center justify-center"></i>
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="搜索演员姓名"
                  className="flex-1 bg-transparent border-0 outline-none text-sm px-2 text-foreground-900 placeholder:text-foreground-400"
                />
              </div>
              <button
                type="submit"
                className="px-4 h-10 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {pageItems.map((member) => (
                  <CastCard key={member.id} member={member} />
                ))}
              </div>
              <div className="mt-8">
                <Pagination current={page} total={totalPages} onChange={setPage} />
              </div>
            </>
          ) : (
            <div className="bg-background-50 border border-dashed border-background-300 rounded-lg py-16 px-6 text-center">
              <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-user-search-line text-3xl"></i>
              </span>
              <h2 className="font-heading font-semibold text-foreground-950 text-lg mb-2">
                没有找到匹配的演员
              </h2>
              <p className="text-sm text-foreground-500 mb-5">
                请检查关键词是否正确，或点击下方按钮查看全部演员。
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={reset}
                  className="px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
                >
                  重置搜索
                </button>
                <Link
                  to="/category/plot"
                  className="px-5 py-2.5 rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:border-accent-400 hover:text-accent-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  去看剧情介绍
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}